import React from "react";
import { ArrowLeft } from "lucide-react";
import { Logo } from "../branding/Logo";

/**
 * Wizard chrome — sticky header with logo, back button, progress bar.
 * Renders `children` inside the phone-sized shell.
 *
 * Props:
 *   step:         1-based step number
 *   total:        total steps (for progress %)
 *   onBack:       callback (undefined → back button hidden)
 *   subtitle:     small eyebrow above the header (optional)
 *   dense:        removes body padding when true (used by full-bleed steps)
 */
export function WizardShell({
    step,
    total,
    onBack,
    subtitle,
    children,
    dense = false,
}) {
    const pct = total > 0 ? Math.round((step / total) * 100) : 0;
    return (
        <div className="meow-shell" data-testid="wizard-shell">
            <header className="sticky top-0 z-20 bg-meow-cream/95 backdrop-blur">
                <div className="flex items-center justify-between px-5 pt-4">
                    <div className="flex items-center gap-3">
                        {onBack ? (
                            <button
                                type="button"
                                onClick={onBack}
                                className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-[0_2px_10px_rgba(42,42,42,0.06)] transition-colors hover:bg-white/90"
                                aria-label="Go back"
                                data-testid="wizard-back-btn"
                            >
                                <ArrowLeft className="h-4 w-4 text-meow-charcoal" strokeWidth={2} />
                            </button>
                        ) : (
                            <Logo />
                        )}
                        {subtitle && (
                            <span className="eyebrow hidden sm:inline" data-testid="wizard-subtitle">
                                {subtitle}
                            </span>
                        )}
                    </div>
                    {total > 0 && (
                        <span
                            className="text-xs font-medium tabular-nums text-meow-muted"
                            data-testid="wizard-step-counter"
                        >
                            {step} / {total}
                        </span>
                    )}
                </div>
                {total > 0 && (
                    <div className="mt-3 px-5 pb-3">
                        <div className="meow-progress-track">
                            <div
                                className="meow-progress-fill"
                                style={{ width: `${pct}%` }}
                                data-testid="wizard-progress-fill"
                            />
                        </div>
                    </div>
                )}
            </header>
            <main
                className={
                    "flex-1 " + (dense ? "" : "px-5 pb-8 pt-2")
                }
                data-testid="wizard-body"
            >
                <div className="step-enter">{children}</div>
            </main>
        </div>
    );
}
